import { useEffect, useState } from 'react';
import request from 'lib/request';
import type { AliasedClusterResponse } from 'types/api-responses';
import type { AliasGroupPillState } from './alias-group-pill';
import {
  computeAliasPillStates,
  type AliasPillInput,
} from './alias-pill-states';

// A cluster member surfaced for the textual "Aliases" list on a tile/row.
export interface AliasName {
  id: number;
  name: string;
  type: string;
}

// Per-entity alias info shared by Match Review + the reconcile tearsheet:
//   - pillState: the "Group N (+ Others)" pill state across the visible set
//     (undefined when the entity has no aliases).
//   - role: 'primary' when the entity is its cluster's canonical (or stands
//     alone), 'alias' when it's a non-canonical member. Drives the
//     "Primary"/"Alias" prefix on the Entity Classification field.
//   - aliases: the OTHER cluster members (names for the textual "Aliases" list).
export interface AliasClusterInfo {
  pillState?: AliasGroupPillState;
  role: 'primary' | 'alias';
  aliases: AliasName[];
}

// Fetches each candidate's alias cluster (GET /entities/:id/aliased_entities) in
// parallel and derives, per candidate: the pill state across the visible set,
// the Primary/Alias role (from the cluster's canonical member), and the peer
// names. Pass an empty list (e.g. while the tearsheet is closed) to skip
// fetching.
//
// The cluster id is the lowest member id (matching AliasedEntity's lowest-id
// canonical), and an entity counts as aliased only when its cluster has >1
// member. Self is always folded in so a single-alias entity is detected even if
// the endpoint omits the entity itself from its own cluster.
export function useAliasClusterStates(
  entityIds: number[]
): Map<number, AliasClusterInfo> {
  const [infos, setInfos] = useState<Map<number, AliasClusterInfo>>(new Map());
  const key = [...entityIds].sort((a, b) => a - b).join(',');

  useEffect(() => {
    const ids = key ? key.split(',').map(Number) : [];
    if (ids.length === 0) {
      setInfos(new Map());
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const clusters = await Promise.all(
        ids.map(async (id) => {
          const res = await request.get<AliasedClusterResponse>(
            `/entities/${id}/aliased_entities`
          );
          return { id, rows: 'error' in res ? [] : (res.members ?? []) };
        })
      );
      if (cancelled) return;

      const visible: AliasPillInput[] = [];
      const clusterSizeByGroup: Record<string, number> = {};
      const roleById = new Map<number, 'primary' | 'alias'>();
      const aliasesById = new Map<number, AliasName[]>();

      clusters.forEach(({ id, rows }) => {
        const members = Array.from(
          new Set([id, ...rows.map((row) => row.entity.id)])
        );
        const aliased = members.length > 1;
        const groupId = aliased ? String(Math.min(...members)) : undefined;
        visible.push({ id, aliasGroupId: groupId });
        if (groupId) clusterSizeByGroup[groupId] = members.length;

        // The cluster's canonical member is its "Primary"; every other member is
        // an "Alias". A standalone entity (no peers) is its own primary.
        const canonicalId = rows.find((row) => row.canonical)?.entity.id;
        roleById.set(id, aliased && canonicalId !== id ? 'alias' : 'primary');

        aliasesById.set(
          id,
          rows
            .filter((row) => row.entity.id !== id)
            .map((row) => ({
              id: row.entity.id,
              name: row.entity.name,
              type: row.entity.type,
            }))
        );
      });

      const pillStates = computeAliasPillStates(visible, clusterSizeByGroup);

      const merged = new Map<number, AliasClusterInfo>();
      ids.forEach((id) => {
        merged.set(id, {
          pillState: pillStates.get(id),
          role: roleById.get(id) ?? 'primary',
          aliases: aliasesById.get(id) ?? [],
        });
      });
      setInfos(merged);
    })();

    return () => {
      cancelled = true;
    };
  }, [key]);

  return infos;
}
